import { type CheckoutFaqConfig, type CheckoutFaqEntry, type CheckoutLandingTemplate } from "./CheckoutVariant";

/**
 * Domyślne FAQ szablonów Hollywood. Leży w data-models, bo czytają je i strona (gdy wariant nie
 * ma własnego `faq`), i edytory w adminie oraz w podglądzie (jako punkt wyjścia do edycji).
 */
export const defaultCheckoutFaq: CheckoutFaqConfig = {
  title: "Najczęściej zadawane pytania",
  entries: [
    {
      question: "Co jeśli mimo starań nie osiągnę efektów?",
      answer:
        "Jeżeli realizujesz plan, raportujesz postępy i pozostajesz w kontakcie z dietetykiem - a mimo to nie widzisz efektów, przedłużamy Twoje prowadzenie bez dodatkowych opłat.\nTy dajesz 50% zaangażowania - my 50% ekspertyzy.",
    },
    {
      question: "Czy dam radę, jeśli mam mało czasu?",
      answer:
        "Tak. Program jest zaprojektowany dla zapracowanych kobiet. Dieta zajmuje ok. 20 minut dziennie, a trening 40 minut. Nie musisz dojeżdżać na siłownię - ćwiczysz w domu.",
    },
    {
      question: "Czy muszę umieć obsługiwać aplikacje?",
      answer:
        "Nie. Aplikacja prowadzi Cię krok po kroku, a funkcje AI działają głosem albo zdjęciem - bez wypełniania tabelek. Jadłospis i plan treningowy dostajesz gotowe, zaraz po ankiecie.",
    },
    {
      question: "Czy aplikacja działa na Androidzie i iPhonie?",
      answer:
        "Tak, na obu. Kroki i treningi synchronizują się z Apple Health na iPhonie i z Health Connect na Androidzie.",
    },
    {
      question: "Jak wygląda płatność? Czy mogę rozłożyć ją na raty?",
      answer:
        "Program możesz opłacić jednorazowo albo w ratach - wariant wybierasz niżej, w koszyku. Płatność jednorazowa jest tańsza od sumy rat.",
    },
    {
      question: "Czy to działa po 35 roku życia i przy problemach hormonalnych?",
      answer:
        "Tak. System powstał z myślą o kobietach 35+. Uwzględniamy zmiany hormonalne i metaboliczne, a dedykowany dietetyk analizuje Twoje badania i dobiera wytyczne suplementacyjne.",
    },
  ],
};

const faqEntry = (question: string): CheckoutFaqEntry => {
  const entry = defaultCheckoutFaq.entries.find((item) => item.question === question);

  if (!entry) {
    throw new Error(`Missing default FAQ entry: ${question}`);
  }

  return entry;
};

/**
 * FAQ Black Hollywood 2026 dla nowych klientek, z makiety kampanii: dwa nowe pytania o sposób
 * płatności, a reszta to odpowiedzi z dotychczasowej strony. Odpowiedzi nie podają kwot, bo
 * ceny różnią się między etapami kampanii, a kwoty i oszczędności stoją w cenniku wyżej.
 */
export const blackFridayFaq: CheckoutFaqConfig = {
  title: defaultCheckoutFaq.title,
  entries: [
    {
      question: "Czym różnią się raty od płatności jednorazowej?",
      answer:
        "Przy 12 miesiącach możesz wybrać pakiet 13 miesięcy: jednorazowo (oszczędzasz najwięcej i dostajesz Domowy Lifting Hollywood) albo w 2 ratach. Przy 6 miesiącach płatność jednorazowa też wychodzi taniej niż raty. Dokładne kwoty i oszczędności widzisz w cenniku.",
    },
    {
      question: "Czy mogę płacić BLIK-iem?",
      answer: "Tak. Przy ratach BLIK cykliczny pobiera ratę automatycznie, tak jak karta.",
    },
    faqEntry("Co jeśli mimo starań nie osiągnę efektów?"),
    faqEntry("Czy dam radę, jeśli mam mało czasu?"),
    faqEntry("Czy to działa po 35 roku życia i przy problemach hormonalnych?"),
    faqEntry("Czy aplikacja działa na Androidzie i iPhonie?"),
  ],
};

/**
 * FAQ Black Hollywood 2026 na przedłużeniach, z makiety „kontynuacja metamorfozy”. Pytanie
 * o rozłożenie płatności jednorazowej (PayPo) pominięte, bo makieta ma je „do sprawdzenia”.
 */
export const blackFridayRenewalFaq: CheckoutFaqConfig = {
  title: defaultCheckoutFaq.title,
  entries: [
    {
      question: "Co daje Rok VIP?",
      answer:
        "13 miesięcy opieki w jednej płatności, taniej niż w ratach. Do tego VIP koszulka Hollywood, 6-tygodniowy Reset Hollywood dla bliskiej osoby i Domowy Lifting Hollywood. Te dodatki są tylko przy płatności jednorazowej.",
    },
    {
      question: "Czy raty nałożą się na moje obecne raty?",
      answer:
        "Przy 12 ratach nie. Pierwszą ratę płacisz dziś, kolejne zaczynają się dopiero po zakończeniu obecnego planu. Przy 2 ratach druga rata schodzi po 30 dniach.",
    },
    {
      question: "Komu mogę dać Reset dla bliskiej osoby?",
      answer:
        "Mamie, siostrze, przyjaciółce albo komukolwiek bliskiemu. Po zakupie dostajesz wejściówkę, którą przekazujesz dalej.",
    },
    {
      question: "Czy zostanę z tym samym dietetykiem?",
      answer: "Tak. Kontynuujesz z tym samym opiekunem, a plan dalej dopasowujemy do Twoich wyników.",
    },
  ],
};

/** FAQ, które strona pokazuje, gdy wariant nie ma własnego `faq`. */
export const defaultFaqFor = (template: CheckoutLandingTemplate | undefined, isRenewal: boolean): CheckoutFaqConfig =>
  template === "hollywoodBlackFriday" ? (isRenewal ? blackFridayRenewalFaq : blackFridayFaq) : defaultCheckoutFaq;
